import {
  defineNavigationQuerySchema,
  navigationQuery,
} from '../../navigation';
import { STUDENT_LIFECYCLE_STATUSES } from '../../types/reception';

export const RECEPTION_LIST_SEARCH_MAX_LENGTH = 120;

export function normalizeReceptionSearch(value: string): string {
  return value.replace(/[٠-٩۰-۹]/g, digit => String(digit.charCodeAt(0) - (digit <= '٩' ? 0x660 : 0x6f0)))
    .slice(0, RECEPTION_LIST_SEARCH_MAX_LENGTH);
}

/**
 * Keep the current student lookup when switching between student categories.
 * Other list filters intentionally remain category-specific.
 */
export function withStudentCategorySearch(
  target: string,
  sourcePathname: string,
  sourceSearch: string,
): string {
  const [targetPathname, targetSearch = ''] = target.split('?', 2);
  if (sourcePathname !== '/reception/students' || targetPathname !== '/reception/students') {
    return target;
  }

  const targetParams = new URLSearchParams(targetSearch);
  if (!targetParams.has('category')) return target;

  const searchQuery = new URLSearchParams(sourceSearch).get('q')?.trim() ?? '';
  if (!searchQuery) return target;

  targetParams.set('q', searchQuery);
  return `${targetPathname}?${targetParams.toString()}`;
}

export const receptionDashboardSchema = defineNavigationQuerySchema({
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 100 }),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: RECEPTION_LIST_SEARCH_MAX_LENGTH }),
  student: navigationQuery.id('student'),
  studentTab: navigationQuery.enum(
    'studentTab',
    ['details', 'groups', 'account', 'actions', 'comments', 'history'] as const,
    'details',
  ),
});

export const receptionStudentsSchema = defineNavigationQuerySchema({
  // Keep legacy single `?category=waiting` links working while allowing more
  // than one lifecycle state to live in the URL.
  category: navigationQuery.stringList(
    'category',
    {
      allowedValues: STUDENT_LIFECYCLE_STATUSES,
      maxItems: STUDENT_LIFECYCLE_STATUSES.length,
      maxItemLength: 20,
    },
  ),
  groupMembership: navigationQuery.enum(
    'groupMembership',
    ['', 'with_group', 'without_group', 'specific'] as const,
    '',
  ),
  groupId: navigationQuery.id('group_id'),
  deliveryMode: navigationQuery.enum('delivery_mode', ['', 'in_person', 'online', 'hybrid'] as const, ''),
  sortBy: navigationQuery.enum('sort_by', ['created_at', 'full_name', 'student_code', 'branch', 'course', 'status'] as const, 'created_at'),
  sortOrder: navigationQuery.enum('sort_order', ['asc', 'desc'] as const, 'desc'),
  flag: navigationQuery.string('flag', { defaultValue: '', maxLength: 40 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 100 }),
  courseType: navigationQuery.enum(
    'course_type',
    ['', 'language', 'non_language'] as const,
    '',
  ),
  course: navigationQuery.string('course', { defaultValue: '', maxLength: 150 }),
  languageStage: navigationQuery.enum('language_stage', ['', 'A1','A2','B1','B2','C1','C2'] as const, ''),
  languageLevel: navigationQuery.string('language_level', {defaultValue:'',maxLength:2}),
  languageLevelKind: navigationQuery.enum('language_level_kind', ['', 'current','last_completed','stopped'] as const, ''),
  languageStatus: navigationQuery.enum('language_status', ['', 'booked','studying','completed','stopped','unknown'] as const, ''),
  languageDateKind: navigationQuery.enum('language_date_kind', ['', 'completed','stopped'] as const, ''),
  languageFrom: navigationQuery.date('language_from'),
  languageTo: navigationQuery.date('language_to'),
  createdFrom: navigationQuery.date('createdFrom'),
  createdTo: navigationQuery.date('createdTo'),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: RECEPTION_LIST_SEARCH_MAX_LENGTH }),
  page: navigationQuery.integer('page', { defaultValue: 1, min: 1, max: 100_000 }),
  pageSize: navigationQuery.enum(
    'pageSize',
    ['10', '25', '50', '100'] as const,
    '25',
  ),
  plain: navigationQuery.boolean('plain', false),
  student: navigationQuery.id('student'),
  studentTab: navigationQuery.enum(
    'studentTab',
    ['details', 'groups', 'account', 'actions', 'comments', 'history'] as const,
    'details',
  ),
});

export const receptionWaitingSchema = defineNavigationQuerySchema({
  q: navigationQuery.string('q', { defaultValue: '', maxLength: RECEPTION_LIST_SEARCH_MAX_LENGTH }),
  month: navigationQuery.string('month', { defaultValue: '', maxLength: 7 }),
  course: navigationQuery.string('course', { defaultValue: '', maxLength: 150 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 100 }),
  status: navigationQuery.enum(
    'status',
    ['all', 'needs', 'covered', 'has_note'] as const,
    'all',
  ),
  date: navigationQuery.date('date'),
  waiting: navigationQuery.string('waiting', { defaultValue: '', maxLength: 512 }),
  createGroup: navigationQuery.string('createGroup', { defaultValue: '', maxLength: 512 }),
});

export type ReceptionStudentTab = 'details' | 'groups' | 'account' | 'actions' | 'comments' | 'history';

export interface WaitingSuggestionIdentity {
  month: string;
  course: string;
  branch: string | null;
}

const WAITING_SUGGESTION_ID_PREFIX = 's1.';
const WAITING_SUGGESTION_ID_MAX_LENGTH = 480;

export function isReceptionMonth(value: string): boolean {
  return /^(20\d{2}|2100)-(0[1-9]|1[0-2])$/u.test(value);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

function base64UrlToBytes(value: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]+$/u.test(value)) return null;
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  try {
    const binary = atob(padded);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
}

export function encodeWaitingSuggestionId(identity: WaitingSuggestionIdentity): string {
  const month = identity.month.trim();
  const course = identity.course.trim();
  const branch = identity.branch?.trim() || null;
  if (!isReceptionMonth(month)
    || !course
    || course.length > 150
    || (branch?.length ?? 0) > 100) {
    return '';
  }
  const encoded = WAITING_SUGGESTION_ID_PREFIX + bytesToBase64Url(
    new TextEncoder().encode(JSON.stringify([month, course, branch])),
  );
  return encoded.length <= WAITING_SUGGESTION_ID_MAX_LENGTH ? encoded : '';
}

export function decodeWaitingSuggestionId(value: string): WaitingSuggestionIdentity | null {
  if (!value.startsWith(WAITING_SUGGESTION_ID_PREFIX)
    || value.length > WAITING_SUGGESTION_ID_MAX_LENGTH) return null;
  const bytes = base64UrlToBytes(value.slice(WAITING_SUGGESTION_ID_PREFIX.length));
  if (!bytes) return null;
  try {
    const tuple = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)) as unknown;
    if (!Array.isArray(tuple) || tuple.length !== 3) return null;
    const [month, course, branch] = tuple;
    if (typeof month !== 'string'
      || typeof course !== 'string'
      || (typeof branch !== 'string' && branch !== null)) return null;
    const identity = { month, course, branch };
    return encodeWaitingSuggestionId(identity) === value ? identity : null;
  } catch {
    return null;
  }
}
